import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { GridView, List, Settings } from '@nike/nike-design-system-icons';
import { Search, Tabs, TabsList, TabsTrigger } from '@nike/podium';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSearch } from '~/atoms/hooks/useSearch';
import { useLinePlanEntity } from '~/hooks/useLinePlanEntity';
import { useSavedViews } from '~/atoms/hooks/useSavedViews';
import { useColumnState } from '~/atoms/hooks/useColumnState';
import { useUser } from '~/atoms/hooks/useUser';
import { gridApiAtom } from '~/atoms/columnState';
import { useCallback, useState } from 'react';
import SaveView from '~/components/SaveView';
import { useNavigate, useParams } from 'react-router-dom';
import DropdownMenuWrapper from '~/components/DropdownMenuWrapper';
import { buildCardSettingsMenu, buildGridSettingsMenu, buildSavedViewsMenu } from '~/utils/buildSettingsMenu';
import { useCardView } from '~/atoms/hooks/useCardView';
import { showToastAtom } from '~/atoms/pageActions';
import { toPluralEntity } from '~/utils/linePlanEntity';
import log from '~/logger';
import { useGridData } from '~/atoms/hooks/useGridData';
import { useSyncProductInstanceCount } from '~/atoms/hooks/useSyncProductInstanceCount';
import { usePrefetchEntityCounts } from '~/atoms/hooks/usePrefetchEntityCounts';
const SearchBox = () => {
    const [searchValue, setSearchValue] = useSearch();
    return (_jsx(Search, { className: "m-2 max-w-48 lg:max-w-64", id: "product-search", "data-testid": "product-search", label: "Search products", placeholder: "Search...", value: searchValue, onChange: (e) => setSearchValue(e.target.value) }));
};
const LayoutViewToggle = () => {
    const [saveViewOpen, setSaveViewOpen] = useState(false);
    const navigate = useNavigate();
    const params = useParams();
    const { linePlanEntity } = useLinePlanEntity();
    const { cardScale, setCardScale, groupBy, setGroupBy, cardSubView, setCardSubView, setCustomGroupsManagerOpen, setGroupOrderManagerOpen } = useCardView();
    const gridApi = useAtomValue(gridApiAtom);
    const { loadSavedView, activeViewId } = useColumnState();
    const { user } = useUser();
    const showToast = useSetAtom(showToastAtom);
    const { menuReadySavedViews, savedViewsLoadingState, openDeleteSavedViewModal } = useSavedViews(true, user?.email, activeViewId);
    const currentEntity = params.linePlanEntity || linePlanEntity;
    const viewType = params.viewType || 'grid';
    const isGrid = viewType === 'grid';
    const isCard = viewType === 'card';
    const goTo = (targetView) => {
        navigate(`/${toPluralEntity(currentEntity)}/view/${targetView}`);
    };
    const handleSelectView = (projectId, viewName) => {
        loadSavedView(projectId);
        showToast({ message: `Switched to ${viewName}`, type: 'success' });
    };
    const savedViewsMenuItem = buildSavedViewsMenu({
        entityType: linePlanEntity,
        menuReadySavedViews,
        savedViewsLoadingState,
        onSelectView: handleSelectView,
        onDeleteView: (viewId, viewName) => openDeleteSavedViewModal({ viewId, viewName }),
        onSaveCurrentView: () => setSaveViewOpen(true)
    });
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("div", { style: {
                            color: isGrid
                                ? 'var(--pds-color-text-primary)'
                                : 'var(--pds-color-text-secondary)'
                        }, className: `${isGrid ? 'cursor-default' : 'cursor-pointer hover:opacity-80'}`, onClick: () => goTo('grid'), role: "button", tabIndex: 0, onKeyDown: (e) => {
                            if (e.key === 'Enter' || e.key === ' ')
                                goTo('grid');
                        }, "aria-label": "Grid view", title: isGrid ? '' : 'Switch to grid view', children: _jsx(List, {}) }), _jsx("div", { style: {
                            color: isCard
                                ? 'var(--pds-color-text-primary)'
                                : 'var(--pds-color-text-secondary)'
                        }, className: `${isCard ? 'cursor-default' : 'cursor-pointer hover:opacity-80'}`, onClick: () => goTo('card'), role: "button", tabIndex: 0, onKeyDown: (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                goTo('card');
                            }
                        }, "aria-label": "Card view", title: isCard ? '' : 'Switch to card view', children: _jsx(GridView, {}) }), isGrid ? (_jsx(DropdownMenuWrapper, { menu: buildGridSettingsMenu({
                            onOpenColumnsPanel: () => {
                                const currentlyOpen = gridApi?.getOpenedToolPanel
                                    ? gridApi.getOpenedToolPanel()
                                    : null;
                                if (currentlyOpen) {
                                    gridApi?.closeToolPanel();
                                    gridApi?.setSideBarVisible(false);
                                }
                                else {
                                    gridApi?.setSideBarVisible(true);
                                    gridApi?.openToolPanel('columns');
                                }
                            },
                            savedViewsMenu: savedViewsMenuItem
                        }), children: _jsx("div", { style: { color: 'var(--pds-color-text-primary)' }, className: "cursor-pointer hover:opacity-80", role: "button", tabIndex: 0, "aria-label": "Grid settings", title: "Grid settings", children: _jsx(Settings, {}) }) })) : (_jsx(DropdownMenuWrapper, { menu: buildCardSettingsMenu({
                            currentScale: cardScale,
                            currentGroupBy: groupBy,
                            currentViewType: cardSubView,
                            onScaleChange: setCardScale,
                            onGroupByChange: setGroupBy,
                            onViewTypeChange: setCardSubView,
                            includeCustomGroupActions: groupBy === 'custom',
                            onAddGroup: () => setCustomGroupsManagerOpen(true),
                            onManageOrder: () => setGroupOrderManagerOpen(true),
                            savedViewsMenu: savedViewsMenuItem
                        }), children: _jsx("div", { style: { color: 'var(--pds-color-text-primary)' }, className: "cursor-pointer hover:opacity-80", role: "button", tabIndex: 0, onKeyDown: (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                }
                            }, "aria-label": "Card tools", children: _jsx(Settings, {}) }) }))] }), _jsx(SaveView, { open: saveViewOpen, onOpenChange: setSaveViewOpen })] }));
};
function isLinePlanEntity(value) {
    return ['project', 'product', 'style', 'expression', 'model'].includes(value);
}
const DataTypeTabs = () => {
    const { linePlanEntity, setLinePlanEntity } = useLinePlanEntity();
    const { entityCounts } = useGridData();
    const navigate = useNavigate();
    const params = useParams();
    const currentViewType = params.viewType || 'grid';
    const handleTabChange = (value) => {
        if (value === 'product') {
            navigate('/product-matrix');
            return;
        }
        if (isLinePlanEntity(value)) {
            setLinePlanEntity(value);
            navigate(`/${toPluralEntity(value)}/view/${currentViewType}`);
        }
        else {
            log.error('Invalid line plan entity', { value });
        }
    };
    const renderTabContent = useCallback((label, value) => {
        const count = entityCounts[value] ?? 0;
        return (_jsxs("span", { children: [label, _jsxs("span", { className: "text-xs opacity-70", children: ["\u00A0(", count.toLocaleString(), ")"] })] }));
    }, [entityCounts]);
    return (_jsx("div", { className: "pt-1 pb-2 w-fit", children: _jsx("div", { className: "flex items-center gap-3", children: _jsx(Tabs, { value: linePlanEntity, onValueChange: handleTabChange, children: _jsxs(TabsList, { variant: "overlay", children: [_jsx(TabsTrigger, { value: "project", children: renderTabContent('Projects', 'project') }), _jsx(TabsTrigger, { value: "expression", children: renderTabContent('Expressions', 'expression') }), _jsx(TabsTrigger, { value: "product", children: renderTabContent('Products', 'product') })] }) }) }) }));
};
const FilterBarWrapper = () => {
    usePrefetchEntityCounts();
    useSyncProductInstanceCount();
    return (_jsx("div", { className: "w-full full-width min-w-96 mx-auto", children: _jsxs("div", { className: "grid grid-flow-row-dense grid-cols-12 gap-0", children: [_jsx("div", { className: "p-2 col-span-7 md:col-span-7 lg:col-span-7", children: _jsx("div", { className: "flex items-center gap-3", children: _jsx(DataTypeTabs, {}) }) }), _jsxs("div", { className: "pt-6 col-span-5 md:col-span-5 lg:col-span-5 flex justify-end items-center", children: [_jsx(SearchBox, {}), _jsx(LayoutViewToggle, {})] })] }) }));
};
export default FilterBarWrapper;
